import {
  ChakraFlex,
  FAIconName,
  FireflyIconButton,
  Icon,
  TagDropdown,
  TagProps,
  Text,
} from '@equip.health/ui';
import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CarePlanNoteProblemResponse } from '~/lib/types/carePlan/carePlans';
import { CarePlanGoalStatus } from '~/lib/types/carePlan/CarePlanGoalStatus';
import {
  buildGoalStatusTagDropdownProps,
  toCarePlanNoteGoalTagDropdownOptions,
} from '~/components/maud/patients/profile/tabs/careplan/CarePlanGoalTagDropdownOptions';
import { useGetProblemTypesGoalStatusesMapQuery } from '~/store/services/refCarePlanEnums';
import { useNoteGoalDescription } from '~/lib/hooks/carePlan/useNoteGoalDescription';
import { PatientNoteGoalDescriptionPanel } from './PatientNoteGoalDescriptionPanel';
import { PatientNoteGoalStatusProviderAvatar } from './PatientNoteGoalStatusProviderAvatar';

type Props = {
  noteExternalId: string;
  problem: CarePlanNoteProblemResponse;
  treatmentEpisodeId: string;
};

export const PatientNoteCoOccurringGoalCard = ({
  noteExternalId,
  problem,
  treatmentEpisodeId,
}: Props) => {
  const goal = problem.goals[0];
  const { carePlanM4MedPsychProblems: isMedPsychProblemsEnabled } =
    useFlags<LDFlags>();

  const { data: problemTypesGoalStatusesMap } =
    useGetProblemTypesGoalStatusesMapQuery();

  const statusDropdownOptions: TagProps[] = useMemo(
    () =>
      toCarePlanNoteGoalTagDropdownOptions(
        problemTypesGoalStatusesMap?.[problem.problemType] ?? [],
        isMedPsychProblemsEnabled,
      ),
    [
      problem.problemType,
      problemTypesGoalStatusesMap,
      isMedPsychProblemsEnabled,
    ],
  );

  const {
    description,
    isDescriptionChangedAndVisible,
    handleRevert,
    handleStatusChange,
    isReverting,
    isUpdateGoalLoading,
    isUpdateGoalSuccess,
    onGoalDescriptionBlur,
    setDescription,
    shouldShowDescriptionBox,
  } = useNoteGoalDescription({
    goal,
    noteExternalId,
    treatmentEpisodeId,
  });

  const isNotAssessed = goal.status.code === CarePlanGoalStatus.NOT_ASSESSED;

  return (
    <ChakraFlex
      bg={isNotAssessed ? 'neutral.background.secondary' : 'white'}
      borderColor={isNotAssessed ? 'transparent' : 'neutral.line'}
      borderRadius="12px"
      borderWidth={isNotAssessed ? '0' : '1px'}
      data-testid={`patient-note-drawer__co-occurring-goal__${goal.id}`}
      direction="column"
      gap="12px"
      padding="14px 18px"
    >
      <ChakraFlex alignItems="center" gap="12px" justifyContent="space-between">
        <Text
          data-testid={`patient-note-drawer__co-occurring-goal-title__${goal.id}`}
          variant="bodyMedium"
        >
          {goal.pathwayGoal.label}
        </Text>
        {isNotAssessed ? (
          <ChakraFlex alignItems="center" gap="6px">
            <Icon
              color="neutral.secondary"
              name={FAIconName.eyeSlashRegular}
              size="md"
            />
            <Text color="neutral.secondary">{goal.status.label}</Text>
            <FireflyIconButton
              ariaLabel={`Revert ${goal.status.label.toLowerCase()} status`}
              icon={FAIconName.penRegular}
              id={`note-co-occurring-goal-${goal.id}-revert-button`}
              isDisabled={isReverting}
              isLoading={isReverting}
              onClick={handleRevert}
              size="small"
              variant="tertiary"
            />
          </ChakraFlex>
        ) : (
          <ChakraFlex alignItems="center" gap="12px">
            <PatientNoteGoalStatusProviderAvatar
              description={goal.description}
              goalId={goal.id}
              noteExternalId={noteExternalId}
              statusHistory={goal.statusHistories?.[0]}
            />
            <TagDropdown
              id={`note-co-occurring-goal-${goal.id}-status-dropdown`}
              menuOptions={statusDropdownOptions}
              onOptionClick={handleStatusChange}
              {...buildGoalStatusTagDropdownProps(
                goal.status.code,
                goal.status.label,
                statusDropdownOptions,
              )}
            />
          </ChakraFlex>
        )}
      </ChakraFlex>
      {!isNotAssessed && shouldShowDescriptionBox && (
        <PatientNoteGoalDescriptionPanel
          description={description}
          isDescriptionChangedAndVisible={isDescriptionChangedAndVisible}
          goalId={goal.id}
          idPrefix="patient-note-co-occurring-goal-card-desc"
          isUpdateGoalLoading={isUpdateGoalLoading}
          isUpdateGoalSuccess={isUpdateGoalSuccess}
          onBlur={onGoalDescriptionBlur}
          onChange={setDescription}
        />
      )}
    </ChakraFlex>
  );
};
